import { RankKit } from "@/components/site/RankKit";
import { BenchmarkPanel } from "@/components/rates/BenchmarkPanel";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { LiveRateRangeValue, LiveRateRangeCard } from "@/components/rates/LiveRateRange";
import { TableOfContents, Section, SectionHeader, CTABand, FAQ, StatRow, Breadcrumbs, InlineCTA } from "@/components/site/Blocks";
import { ProsCons, TLDR, ExpertInsight } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { buildHead } from "@/lib/seo-head";
import { getBenchmarkRates } from "@/lib/fred.functions";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/dscr-loan-rates";
const HERO = getDscrClusterHero(URL);

const benchmarksOptions = queryOptions({
  queryKey: ["benchmarkRates"],
  queryFn: () => getBenchmarkRates(),
  staleTime: 60 * 60 * 1000,
  retry: 1,
});

const KEYWORDS = [
  "DSCR loan rates",
  "DSCR loan rates today",
  "current DSCR loan rates",
  "DSCR interest rates",
  "DSCR refinance rates",
  "DSCR loan rates Florida",
  "DSCR loan rates Colorado",
  "30 year fixed DSCR rates",
  "DSCR ARM rates",
  "interest only DSCR rates",
  "investment property loan rates",
  "rental property mortgage rates",
  "DSCR rate by LTV",
  "DSCR rate by credit score",
  "no income verification loan rates",
].join(", ");

const FAQS = [
  {
    q: "How much higher are DSCR loan rates than conventional rates?",
    a: "DSCR pricing typically runs roughly 1.00–2.00 percentage points above a comparable 30-year conforming benchmark. The spread compensates for no income documentation, entity vesting, and investor-property risk. Your actual spread depends on DSCR, LTV, FICO, and property type.",
  },
  {
    q: "What DSCR gets the best rate?",
    a: "A DSCR of 1.25 or higher generally reaches the best-pricing tier. Between 1.00 and 1.24 is the standard tier. Below 1.00 moves into No-Ratio pricing, which carries a premium and a lower maximum LTV.",
  },
  {
    q: "Does credit score change a DSCR rate?",
    a: "Yes. 720+ is the preferred tier, 700–719 is mid-tier, and 660 is the general program floor. Lower scores are workable with more down payment and reserves but price higher.",
  },
  {
    q: "Are DSCR rates fixed or adjustable?",
    a: "Both are available: 30-year fixed, 40-year fixed with an interest-only period, and 5/6 or 7/6 ARMs indexed to SOFR. Fixed carries a slightly higher start rate; ARMs suit investors planning to sell or refinance within the fixed window.",
  },
  {
    q: "Can I buy down a DSCR rate?",
    a: "Yes. Discount points are available on most DSCR programs. Whether a buydown pays off depends on how long you plan to hold the property and the prepayment penalty structure you select.",
  },
  {
    q: "Do prepayment penalties affect the rate?",
    a: "They do. A longer prepay structure (for example 5-year step-down) usually prices better than a shorter or zero-prepay option. We can show you both so you can weigh flexibility against rate.",
  },
  {
    q: "Do DSCR rates vary by state?",
    a: "Yes. Some states carry title, escrow, or licensing cost differences that show up in the total closing figure even when the note rate itself is unaffected. A handful of states also have property types or entity structures with extra requirements that can shift pricing.",
  },
  {
    q: "Does loan amount affect the rate?",
    a: "It can. Very small balances (under roughly $100,000) often price higher because fixed origination and servicing costs are spread over less principal, and very large balances above standard limits may carry their own adjustment.",
  },
  {
    q: "Do short-term rentals get worse pricing?",
    a: "Generally yes. Short-term rental DSCR programs cap around 75% LTV and typically carry a rate add relative to a long-term-lease property, reflecting the income volatility of nightly and weekly bookings.",
  },
  {
    q: "Does property type change my rate?",
    a: "Yes. Single-family and warrantable condos generally price best, while 2–4 unit properties, non-warrantable condos, and condotels typically carry a rate or leverage adjustment for the added complexity and liquidity risk.",
  },
  {
    q: "Can I lock a DSCR rate before the appraisal is done?",
    a: "Some programs allow a preliminary lock once the loan file is substantially complete, but final pricing is confirmed after the appraisal supports the rent and value used to underwrite the file.",
  },
  {
    q: "Why did my quoted rate change between application and closing?",
    a: "Pricing is tied to daily market movement plus your final credit pull, appraisal, and DSCR calculation. A material change in any of those inputs, or a market move before you lock, can shift the number you originally saw.",
  },
];

export const Route = createFileRoute("/dscr-loan-rates")({
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(benchmarksOptions).catch(() => {});
  },
  head: () =>
    buildHead({
      title: "DSCR Loan Rates Today — Live Pricing by DSCR, LTV & FICO",
      description:
        "Live DSCR loan pricing for rental property investors, how DSCR ratio, LTV, and credit tiers shape it, and the market benchmarks that move pricing.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "DSCR Loan Rates", url: URL },
      ],
      service: { name: "DSCR Loan Rates & Pricing" },
      faqs: FAQS,
    }),
  component: DscrRates,
});

function Benchmarks() {
  const { data } = useSuspenseQuery(benchmarksOptions);
  return <BenchmarkPanel data={data}  />;
}

const TIERS: { tier: string; dscr: string; ltv: string; fico: string; note: string }[] = [
  { tier: "Best pricing", dscr: "1.25+", ltv: "65–70%", fico: "720+", note: "30% down preferred; strongest pricing tier with the fewest points." },
  { tier: "Standard", dscr: "1.00–1.24", ltv: "75%", fico: "700–719", note: "The most common DSCR scenario; 25% down." },
  { tier: "Higher leverage", dscr: "1.15+", ltv: "80%", fico: "720+", note: "Maximum standard LTV; stricter reserves. 85% is exception-only at 740+ FICO on an SFR." },
  { tier: "No-Ratio", dscr: "0.75–0.99", ltv: "70–75%", fico: "680+", note: "Property doesn't fully cover PITIA; larger down payment." },
  { tier: "Credit-story", dscr: "1.00+", ltv: "70%", fico: "620–659", note: "30%+ down, strong reserves, compensating factors." },
];

const PRODUCT_ROWS: { product: string; terms: string }[] = [
  { product: "30-Year Fixed DSCR", terms: "Fully amortized · no balloon" },
  { product: "40-Year Fixed (10-yr IO)", terms: "Interest-only 10 years, then 30-yr amortization" },
  { product: "5/6 ARM DSCR", terms: "Fixed 5 years, then SOFR-indexed" },
  { product: "7/6 ARM DSCR", terms: "Fixed 7 years, then SOFR-indexed" },
  { product: "DSCR Cash-Out Refinance", terms: "Max 75% LTV at the strongest tier" },
  { product: "Short-Term Rental (STR) DSCR", terms: "Max 75% LTV; T-12 actuals or projections" },
];

function DscrRates() {
  return (
    <SiteShell hideBreadcrumbs productLabel="DSCR Rates">
      <PageHero
        eyebrow="DSCR · Rates"
        title="DSCR loan rates today"
        subtitle="DSCR rates are priced off the property's cash flow, your leverage, and your credit — not your tax returns. Below are live pricing, how each tier is structured, the market benchmarks, and the levers that actually move your number."
        image={HERO}
        primaryHref="/apply?product=dscr"
      />
      <RankKit page="dscr-loan-rates" part="answer" />
      <PageEssentials hideAnswer hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "DSCR Loan Rates" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#tiers", label: "Pricing tiers" },
          { href: "#by-product", label: "Rates by product" },
          { href: "#what-moves-rates", label: "What moves your rate" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <TLDR
        id="tldr"
        points={[
          "DSCR rates are priced off the property's cash flow, your leverage (LTV), and credit — not personal income or tax returns.",
          "Today's actual pricing comes from the live pricing engine below — no typed-in rate bands; ARMs and interest-only price differently.",
          "The biggest levers are DSCR ratio, LTV, FICO, property type, and whether the loan is a purchase, rate & term, or cash-out.",
          "No-Ratio structures (DSCR below 1.00) trade a higher rate and lower LTV for qualifying without covering PITIA.",
          "Live figures are not a quote, rate lock, offer, or commitment to lend — binding pricing follows underwriting.",
        ]}
      />

      <LiveDscrRateSection
            strategyCards
        id="live-rates"
        eyebrow="Live pricing"
        heading="Live DSCR Pricing"
        paragraphs={[
          "The block below is actual pricing returned by the pricing engine, priced with lender fees in on a 30-day lock, with the rate, APR, points or lender credit and payment on each row all drawn from the same quote.",
          "The tier tables further down explain how DSCR, LTV and FICO shape pricing; the live block shows what the market is actually pricing now. Pricing moves intraday.",
        ]}
        faqs={[
          { q: "Why show tiers and live pricing on the same page?", a: "Tiers explain how pricing is structured across scenarios. Live pricing shows one specific scenario at one point in time. Both are useful, and neither is a rate lock or a commitment to lend." },
          { q: "Can I get today's exact rate for my property?", a: "Price the scenario in the rate center for live indications, then submit the property details for written pricing from a loan officer." },
        ]}
        links={[
          { to: "/investment-property-rates", label: "Open the DSCR rate center" },
          { to: "/dscr-cash-out-refinance", label: "DSCR cash-out refinance pricing" },
          { to: "/dscr-calculator", label: "Calculate your DSCR ratio" },
        ]}
      />

      <Section>
        <div className="mb-8">
          <StatRow
            items={[
              { label: "Live 30-yr fixed range", value: <LiveRateRangeValue /> },
              { label: "Best-tier DSCR", value: "1.25+" },
              { label: "Max standard LTV", value: "80%" },
              { label: "Min FICO", value: "660" },
            ]}
          />
        </div>
        <Benchmarks />
      </Section>

      <RankKit page="dscr-loan-rates" part="details" />

      <Section id="program-samples">
        <SectionHeader
          eyebrow="Live scenario samples"
          title="How structure changes DSCR pricing"
          intro="Each card is one sample scenario priced by our pricing engine in the same scheduled run as the standard sample, changing a single input. They illustrate direction, not all-market pricing."
        />
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {(["fourUnit", "interestOnly", "lowDscr"] as const).map((p) => (
            <div key={p} className="rounded-2xl border border-border bg-card p-6">
              <LiveRateRangeCard program={p} />
            </div>
          ))}
        </div>
      </Section>

      <Section id="tiers">
        <SectionHeader
          eyebrow="Pricing tiers"
          title="How DSCR pricing tiers are structured"
          intro="Tier guidelines for a 1–4 unit non-owner-occupied rental in our lending footprint. For today's actual rates, see the live pricing above — nothing here is a quote, lock, offer, or commitment to lend."
        />
        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">DSCR</th>
                <th className="px-4 py-3">LTV</th>
                <th className="px-4 py-3">FICO</th>
                <th className="px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {TIERS.map((t) => (
                <tr key={t.tier}>
                  <td className="px-4 py-3 font-semibold text-foreground">{t.tier}</td>
                  <td className="px-4 py-3 font-semibold text-brand">{t.dscr}</td>
                  <td className="px-4 py-3 text-foreground">{t.ltv}</td>
                  <td className="px-4 py-3 text-foreground">{t.fico}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <div className="bg-muted/40">
        <Section id="by-product">
          <SectionHeader eyebrow="By loan structure" title="DSCR loan structures" />
          <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Structure</th>
                    <th className="px-4 py-3">Terms</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {PRODUCT_ROWS.map((r) => (
                  <tr key={r.product}>
                    <td className="px-4 py-3 font-semibold text-foreground">{r.product}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.terms}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      <Section id="what-moves-rates">
        <SectionHeader eyebrow="The levers" title="What actually moves your DSCR rate" />
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {[
            ["DSCR ratio", "Every 0.05 below the 1.25 best-pricing threshold generally adds pricing. Raising rent, lowering the loan amount, or switching to interest-only can move you a tier."],
            ["Loan-to-value", "65–70% LTV prices best. 80% is the standard maximum and costs more; 85% is exception-only at 740+ FICO on a single-family rental."],
            ["Credit score", "720+ preferred, 660 floor. Pulling a score across a 20-point tier line is often the cheapest rate improvement available."],
            ["Prepayment structure", "A 5-year step-down prepay prices better than a 1-year or zero-prepay option. Match the term to your actual hold period."],
            ["Property type & use", "Single-family long-term rentals price tightest. Short-term rentals, condotels, and 5–8 unit assets carry add-ons."],
            ["Purpose", "Purchase and rate-and-term price below cash-out. Cash-out caps at 75% LTV on the strongest tier."],
          ].map(([title, body]) => (
            <div key={title} className="rounded-xl border border-border bg-card p-5">
              <div className="font-display text-base font-semibold text-foreground">{title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      <ProsCons
        title="Trade-offs of DSCR pricing"
        pros={[
          "No tax returns, W-2s, or DTI calculation",
          "Close in an LLC without a pricing premium",
          "Interest-only options that materially improve cash flow",
          "No cap on the number of financed properties",
        ]}
        cons={[
          "Rates sit above owner-occupied conventional pricing",
          "Prepayment penalties are standard on the best-priced options",
          "Higher leverage requires stronger DSCR and reserves",
          "Cash-out is capped below purchase leverage",
        ]}
      />

      <InlineCTA
        variant="soft"
        title="Want your actual band instead of a range?"
        body="Send the address, rent, and purchase price. We'll return a scenario review with the tier your deal lands in."
        href="/apply?product=dscr"
      />


      <DscrProgramLinks currentPath={URL} />

      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="DSCR rate questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          All rates and terms shown are illustrative, subject to change without notice, and are not a quote, rate lock,
          offer, or commitment to lend. Binding figures appear on your term sheet after underwriting. Business-purpose
          loans only.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="dscr-rates" />

      <CTABand
        title="Get your DSCR pricing in writing"
        body="Same-business-day scenario review on complete submissions."
        href="/apply?product=dscr"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
